import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Button, Card, CardContent,
  Snackbar, Collapse, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import { dppService } from '../services/dppService';
import { pactFootprintService } from '../services/pactFootprintService';

function DPPCompilation() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  // Selected product state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [pactCompanyIds, setPactCompanyIds] = useState([]);

  // ESPR metadata form
  const [metadata, setMetadata] = useState({
    batch_id: '',
    repairability_score: '',
    durability_score: '',
    hazardous_substances_json: '',
    material_composition_json: '',
  });
  const [metadataSaved, setMetadataSaved] = useState(false);
  const [savingMetadata, setSavingMetadata] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  // Section expansion
  const [section2Open, setSection2Open] = useState(false);
  const [section3Open, setSection3Open] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const result = await dppService.getProducts({ search, limit: 100 });
      setProducts(result.products || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleSelectProduct = async (product) => {
    setSelectedProduct(product);
    setSubmissionResult(null);
    setMetadataSaved(false);
    setSection2Open(true);
    setSection3Open(false);
    setPactCompanyIds([]);

    try {
      const detail = await dppService.getProductDetail(product.product_footprint_id);
      setProductDetail(detail);

      // Fetch the PACT export to surface LEI-enriched companyIds (Phase 1, GLEIF/vLEI)
      pactFootprintService.getFootprint(product.product_footprint_id)
        .then((fp) => setPactCompanyIds(fp.companyIds || []))
        .catch(() => setPactCompanyIds([]));

      // Pre-populate metadata if exists
      if (detail.dpp_metadata) {
        const m = detail.dpp_metadata;
        setMetadata({
          batch_id: m.batch_id || '',
          repairability_score: m.repairability_score || '',
          durability_score: m.durability_score || '',
          hazardous_substances_json: m.hazardous_substances_json
            ? (typeof m.hazardous_substances_json === 'string'
              ? m.hazardous_substances_json
              : JSON.stringify(m.hazardous_substances_json, null, 2))
            : '',
          material_composition_json: m.material_composition_json
            ? (typeof m.material_composition_json === 'string'
              ? m.material_composition_json
              : JSON.stringify(m.material_composition_json, null, 2))
            : '',
        });
        setMetadataSaved(true);
      } else {
        // Suggest URN-based batch_id
        const suggestedBatch = `urn:batch:${product.company_name?.replace(/\s+/g, '-').toLowerCase() || 'unknown'}:${product.product_name_company?.replace(/\s+/g, '-').toLowerCase() || 'product'}:${new Date().toISOString().slice(0, 10)}`;
        setMetadata({
          batch_id: suggestedBatch,
          repairability_score: '',
          durability_score: '',
          hazardous_substances_json: '',
          material_composition_json: '',
        });
        setMetadataSaved(false);
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to load product detail: ${err.message}`, severity: 'error' });
    }
  };

  const handleSaveMetadata = async () => {
    if (!metadata.batch_id) {
      setSnackbar({ open: true, message: 'Batch ID is required', severity: 'warning' });
      return;
    }

    try {
      setSavingMetadata(true);
      const payload = {
        product_footprint_id: selectedProduct.product_footprint_id,
        batch_id: metadata.batch_id,
        repairability_score: metadata.repairability_score ? parseFloat(metadata.repairability_score) : null,
        durability_score: metadata.durability_score ? parseFloat(metadata.durability_score) : null,
        hazardous_substances_json: metadata.hazardous_substances_json ? JSON.parse(metadata.hazardous_substances_json) : null,
        material_composition_json: metadata.material_composition_json ? JSON.parse(metadata.material_composition_json) : null,
      };
      await dppService.createMetadata(payload);
      setMetadataSaved(true);
      setSection3Open(true);
      setSnackbar({ open: true, message: 'ESPR metadata saved', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Save failed: ${err.response?.data?.error?.message || err.message}`, severity: 'error' });
    } finally {
      setSavingMetadata(false);
    }
  };

  const handleSubmitClaim = async () => {
    try {
      setSubmitting(true);
      const result = await dppService.submitClaim(selectedProduct.product_footprint_id);
      setSubmissionResult(result);
      setSnackbar({ open: true, message: 'DPP claim submitted to dVE', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Submission failed: ${err.response?.data?.error?.message || err.message}`, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const getDppStatusChip = (status) => {
    const colors = {
      PENDING: 'warning',
      VERIFIED: 'success',
      ISSUED: 'info',
      REJECTED: 'error',
      FLAGGED: 'warning',
    };
    return <Chip label={status || 'None'} size="small" color={colors[status] || 'default'} variant="outlined" />;
  };

  if (loading && products.length === 0) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>DPP Compilation</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Step 1: Select a product footprint from Open Footprint, enter ESPR-specific metadata, and submit the DPP claim for verification.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Section 1: Product Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>1. Select Product</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" label="Search products"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              placeholder="Search by product name..."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              {total} product footprint(s) found in Open Footprint
            </Typography>
          </Grid>
        </Grid>

        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Product Name</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>PCF Value</TableCell>
                <TableCell>OFP Status</TableCell>
                <TableCell>DPP Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow
                  key={product.product_footprint_id}
                  hover
                  selected={selectedProduct?.product_footprint_id === product.product_footprint_id}
                  onClick={() => handleSelectProduct(product)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{product.product_name_company}</TableCell>
                  <TableCell>{product.company_name}</TableCell>
                  <TableCell>
                    {product.pcf_excluding_biogenic_uptake
                      ? `${product.pcf_excluding_biogenic_uptake} kgCO2e/${product.declared_unit_of_measurement || 'unit'}`
                      : '-'}
                  </TableCell>
                  <TableCell><Chip label={product.status} size="small" variant="outlined" /></TableCell>
                  <TableCell>{getDppStatusChip(product.dpp_status)}</TableCell>
                </TableRow>
              ))}
              {products.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No product footprints found. Ingest product data into Open Footprint first.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Section 2: ESPR Metadata */}
      {selectedProduct && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">2. ESPR Metadata</Typography>
            <IconButton onClick={() => setSection2Open(!section2Open)} size="small">
              {section2Open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Product: {selectedProduct.product_name_company} ({selectedProduct.company_name})
          </Typography>
          {pactCompanyIds.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">Company IDs:</Typography>
              {pactCompanyIds.map((id) => {
                const isLei = String(id).toLowerCase().startsWith('urn:lei:');
                return (
                  <Chip
                    key={id}
                    label={isLei ? `LEI: ${id.slice('urn:lei:'.length)}` : id}
                    size="small"
                    color={isLei ? 'success' : 'default'}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          )}

          <Collapse in={section2Open}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth size="small" label="Batch ID (URN)"
                  value={metadata.batch_id}
                  onChange={(e) => { setMetadata({ ...metadata, batch_id: e.target.value }); setMetadataSaved(false); }}
                  placeholder="urn:batch:org:product:2026-05-25"
                  helperText="Unique batch identifier in URN format"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth size="small" label="Repairability Score" type="number"
                  value={metadata.repairability_score}
                  onChange={(e) => { setMetadata({ ...metadata, repairability_score: e.target.value }); setMetadataSaved(false); }}
                  inputProps={{ min: 0, max: 5, step: 0.1 }}
                  helperText="0.0 - 5.0"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth size="small" label="Durability Score" type="number"
                  value={metadata.durability_score}
                  onChange={(e) => { setMetadata({ ...metadata, durability_score: e.target.value }); setMetadataSaved(false); }}
                  inputProps={{ min: 0, max: 5, step: 0.1 }}
                  helperText="0.0 - 5.0"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth size="small" label="Hazardous Substances (JSON)" multiline rows={3}
                  value={metadata.hazardous_substances_json}
                  onChange={(e) => { setMetadata({ ...metadata, hazardous_substances_json: e.target.value }); setMetadataSaved(false); }}
                  placeholder='{"substances": [{"name": "...", "cas": "...", "concentration_pct": 0.5}]}'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth size="small" label="Material Composition (JSON)" multiline rows={3}
                  value={metadata.material_composition_json}
                  onChange={(e) => { setMetadata({ ...metadata, material_composition_json: e.target.value }); setMetadataSaved(false); }}
                  placeholder='{"materials": [{"name": "Steel", "percentage": 85}, {"name": "Copper", "percentage": 15}]}'
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained" startIcon={<SaveIcon />}
                  onClick={handleSaveMetadata} disabled={savingMetadata || !metadata.batch_id}
                >
                  {savingMetadata ? 'Saving...' : 'Save Metadata'}
                </Button>
                {metadataSaved && (
                  <Chip label="Saved" color="success" size="small" sx={{ ml: 2 }} />
                )}
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
      )}

      {/* Section 3: Submit Claim */}
      {selectedProduct && metadataSaved && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">3. Review & Submit Claim</Typography>
            <IconButton onClick={() => setSection3Open(!section3Open)} size="small">
              {section3Open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={section3Open}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">Product</Typography>
                    <Typography variant="body1">{selectedProduct.product_name_company}</Typography>
                    <Typography variant="body2" color="text.secondary">{selectedProduct.company_name}</Typography>
                    {productDetail?.pcf_excluding_biogenic_uptake && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        PCF: {productDetail.pcf_excluding_biogenic_uptake} kgCO2e/{productDetail.declared_unit_of_measurement || 'unit'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">ESPR Metadata</Typography>
                    <Typography variant="body2">Batch: {metadata.batch_id}</Typography>
                    {metadata.repairability_score && <Typography variant="body2">Repairability: {metadata.repairability_score}/5.0</Typography>}
                    {metadata.durability_score && <Typography variant="body2">Durability: {metadata.durability_score}/5.0</Typography>}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                {submissionResult ? (
                  <Alert severity="success">
                    DPP claim submitted successfully. Verification session: {submissionResult.verification_session_id}
                  </Alert>
                ) : (
                  <Button
                    variant="contained" color="primary" startIcon={<SendIcon />}
                    onClick={handleSubmitClaim} disabled={submitting}
                    size="large"
                  >
                    {submitting ? 'Submitting to dVE...' : 'Submit DPP Claim to dVE'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

export default DPPCompilation;
